<script lang="ts">
  // First-join welcome, the player builds their opening collection by choosing ONE
  // ready-to-duel Structure Deck from EACH era: a Season 1 (Yugi-era) deck and a GX-era
  // deck. Real, complete, themed 40-card decks (the authentic Yu-Gi-Oh onboarding), not
  // boosters to crack. Confirming grants + saves both, then drops them into the world.
  //
  // The flow is select-then-confirm (not click-to-claim): each era is its own group, you
  // pick one art-forward tile per group (its cyan edge stays lit + a check badge appears),
  // and a single confirm bar, enabled once both eras are satisfied, claims them together.
  import { fade, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import type { StarterDeck, StarterEra, GrantedStarterDeck } from "@ygoevo/world-protocol";
  import { send, listen } from "../stores/net.svelte.ts";
  import { cardArt, cardImage } from "../../data.ts";
  import DataWindow from "./ui/DataWindow.svelte";

  let {
    choices,
    onDone,
  }: {
    choices: StarterDeck[];
    /** Called once the player has claimed (and dismissed) their decks. */
    onDone: () => void;
  } = $props();

  // Era display order + copy (chronological: origins → GX). Only eras actually present
  // in `choices` are rendered, so adding/removing an era server-side just works.
  const ERA_META: Record<StarterEra, { label: string; tag: string }> = {
    season1: { label: "Season 1", tag: "Classic Duel Monsters structure decks" },
    gx: { label: "GX Era", tag: "Later structure decks" },
  };
  const ERA_ORDER: StarterEra[] = ["season1", "gx"];
  const groups = $derived(
    ERA_ORDER.map((era) => ({ era, decks: choices.filter((d) => d.era === era) })).filter((g) => g.decks.length),
  );

  // One pick per era. `selected[era] = deckId`.
  let selected = $state<Partial<Record<StarterEra, string>>>({});
  const chosenCount = $derived(groups.filter((g) => selected[g.era]).length);
  const allChosen = $derived(groups.length > 0 && chosenCount === groups.length);

  let claiming = $state(false);
  let granted = $state<GrantedStarterDeck[] | null>(null);
  let error = $state("");

  // Hero artwork → fall back to the self-hosted full card if the cropped art 404s.
  const heroArt = (d: StarterDeck): string => (d.art == null ? "" : cardArt(d.art));
  const onArtError = (e: Event, d: StarterDeck) => {
    if (d.art != null) (e.currentTarget as HTMLImageElement).src = cardImage(d.art);
  };
  // Resolve a granted deck back to its offer tile (for the acquired-view hero art).
  const choiceFor = (deckId: string) => choices.find((c) => c.id === deckId);
  // Merged, de-duplicated card art for the "decks acquired" preview.
  const acquiredCards = $derived(granted ? [...new Set(granted.flatMap((g) => g.cardIds))] : []);

  function toggle(d: StarterDeck) {
    if (claiming) return;
    error = "";
    selected = { ...selected, [d.era]: d.id };
  }

  function confirm() {
    if (claiming || !allChosen) return;
    claiming = true;
    error = "";
    send({ t: "ClaimStarterDeck", deckIds: groups.map((g) => selected[g.era]!) });
  }

  listen("StarterDeckGranted", (m) => {
    claiming = false;
    granted = m.decks?.length ? m.decks : null;
    if (!granted) onDone();
  });

  listen("Error", (m) => {
    if (!claiming) return;
    claiming = false;
    error = m.message || "Couldn't claim those decks, try again.";
  });
</script>

<div class="mobile-dialog-host fixed inset-0 z-[60] grid place-items-center p-4" role="presentation">
  <div class="modal-scrim absolute inset-0 bg-void/55" transition:fade={{ duration: 220 }}></div>

  {#if !granted}
    <div
      class="mobile-full-dialog relative w-[min(1000px,96vw)]"
      role="dialog"
      aria-modal="true"
      aria-label="Choose your starter decks"
      transition:scale={{ start: 0.96, opacity: 0, duration: 260, easing: expoOut }}
    >
      <DataWindow c={18} fill class="mobile-dialog-panel">
        <div class="shrink-0 px-6 pt-7 text-center phone:px-4 phone:pt-3">
          <div class="font-display text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-cyan" style="text-shadow:0 0 12px color-mix(in oklab,var(--color-cyan) 55%,transparent)">Welcome, Duelist</div>
          <h2 class="mt-1.5 font-display text-[1.7rem] font-bold leading-tight text-ink phone:mt-0.5 phone:text-[1.15rem]" style="text-wrap:balance">Choose your starter decks</h2>
          <p class="mx-auto mt-2 max-w-[60ch] text-sm leading-relaxed text-ink-dim phone:mt-1 phone:text-xs phone:leading-snug" style="text-wrap:pretty">
            Pick <strong class="text-ink">one deck from each era</strong>, two complete, ready-to-duel decks to begin your collection. You can refine them any time in the Deck Editor.
          </p>
        </div>

        <div class="groups mobile-dialog-scroll scroll-cyber">
          {#each groups as g (g.era)}
            {@const meta = ERA_META[g.era]}
            {@const done = !!selected[g.era]}
            <section class="group-block" aria-labelledby="era-{g.era}">
              <header class="era-head">
                <div class="era-titles" id="era-{g.era}">
                  <span class="era-label">{meta.label}</span>
                  <span class="era-tag">{meta.tag}</span>
                </div>
                <span class="era-status" class:done aria-hidden="true">
                  {#if done}<span class="tick">✓</span> Chosen{:else}Pick one{/if}
                </span>
              </header>

              <div class="deck-row" role="radiogroup" aria-label="{meta.label} decks">
                {#each g.decks as d, i (d.id)}
                  {@const isSel = selected[d.era] === d.id}
                  <button
                    type="button"
                    class="tile chamfer group"
                    class:selected={isSel}
                    class:dimmed={done && !isSel}
                    style="--c:11px;--i:{i}"
                    onclick={() => toggle(d)}
                    disabled={claiming}
                    role="radio"
                    aria-checked={isSel}
                    aria-label="{d.name}, {meta.label} deck"
                  >
                    {#if d.art != null}
                      <img class="art" src={heroArt(d)} alt="" draggable="false" loading="lazy" onerror={(e) => onArtError(e, d)} />
                    {:else}
                      <div class="art art-empty"></div>
                    {/if}
                    <div class="scrim"></div>
                    <span class="corner"></span>

                    <span class="check" aria-hidden="true">✓</span>

                    <div class="meta">
                      <span class="name">{d.name}</span>
                    </div>

                    <span class="scan"></span>
                  </button>
                {/each}
              </div>
            </section>
          {/each}
        </div>

        <!-- Confirm bar: progress hint + a single claim CTA, enabled once both eras are chosen.
             shrink-0 so the CTA is never squeezed out of a short (landscape phone) shell. -->
        <div class="confirm-bar shrink-0">
          {#if error}
            <span class="hint err" role="alert">{error}</span>
          {:else}
            <span class="hint" aria-live="polite">
              {#if allChosen}Ready, claim your two starter decks{:else}{chosenCount} of {groups.length} chosen · pick one from each era{/if}
            </span>
          {/if}
          <button
            type="button"
            class="claim-cta chamfer-btn"
            style="--c:9px"
            onclick={confirm}
            disabled={!allChosen || claiming}
          >
            {#if claiming}
              <span class="ring small"></span> Building decks…
            {:else}
              Claim decks
            {/if}
          </button>
        </div>
      </DataWindow>
    </div>
  {:else}
    {@const g = granted}
    <div
      class="mobile-full-dialog relative w-[min(860px,94vw)]"
      role="dialog"
      aria-modal="true"
      aria-label="Decks acquired"
      transition:scale={{ start: 0.95, opacity: 0, duration: 280, easing: expoOut }}
    >
      <DataWindow c={18} fill class="mobile-dialog-panel">
        <div class="shrink-0 px-6 pt-6 text-center phone:px-4 phone:pt-3">
          <div class="font-display text-[0.66rem] font-semibold uppercase tracking-[0.26em] text-lime" style="text-shadow:0 0 12px color-mix(in oklab,var(--color-lime) 50%,transparent)">Decks Acquired</div>
          <h2 class="mt-1 font-display text-[1.6rem] font-bold leading-tight text-ink phone:text-[1.15rem]" style="text-wrap:balance">Your collection has begun</h2>
        </div>

        <!-- Body: deck summaries + the merged card grid share ONE scroll region, so the
             "Enter Domino City" button below stays pinned and reachable on a short shell. -->
        <div class="mobile-dialog-scroll max-h-[52dvh] min-h-0 flex-1 overflow-y-auto scroll-cyber">
          <!-- One summary row per granted deck (hero art + name + size). -->
          <div class="acquired-list px-6 pt-4 phone:px-3">
            {#each g as deck (deck.deckId)}
              {@const c = choiceFor(deck.deckId)}
              <div class="acquired-row">
                <div class="acquired-hero chamfer" style="--c:8px">
                  {#if c?.art != null}
                    <img src={heroArt(c)} alt="" class="size-full object-cover" draggable="false" onerror={(e) => onArtError(e, c)} />
                  {/if}
                </div>
                <div class="min-w-0">
                  <span class="acquired-era">{ERA_META[deck.era].label}</span>
                  <div class="acquired-name">{deck.name}</div>
                  <div class="acquired-count">{deck.cardIds.length}-card deck, saved &amp; ready to duel</div>
                </div>
              </div>
            {/each}
          </div>

          <div class="mt-4 grid grid-cols-7 gap-1.5 px-6 pb-1 sm:grid-cols-9 phone:px-3">
            {#each acquiredCards as id (id)}
              <img src={cardImage(id)} alt="" class="aspect-[59/86] w-full chamfer object-cover ring-1 ring-cyan/15" style="--c:3px" draggable="false" loading="lazy" />
            {/each}
          </div>
        </div>

        <div class="mt-2 shrink-0 border-t border-cyan-dim/25 p-4 text-center phone:mt-0 phone:p-2.5">
          <button
            onclick={onDone}
            class="chamfer-btn border border-cyan/55 bg-surface-2/70 px-10 py-2.5 font-display text-sm font-bold uppercase tracking-[0.16em] text-cyan transition-colors hover:bg-cyan/15 hover:glow-cyan"
            style="--c:8px"
          >
            Enter Domino City
          </button>
        </div>
      </DataWindow>
    </div>
  {/if}
</div>

<style>
  /* Scroll container holding both era groups. `dvh` (not vh) so a mobile browser's
     URL bar can't push the confirm bar below the fold. */
  .groups {
    max-height: min(62dvh, 560px);
    overflow-y: auto;
    padding: 1.1rem 1.5rem 0.4rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  /* Landscape phone: the eras sit SIDE BY SIDE. A phone is wide and short, so two
     columns spend the spare width instead of forcing a vertical scroll past the
     first era — the whole choice fits on one screen. */
  :global(.phone) .groups {
    padding: 0.6rem 0.75rem 0.3rem;
    flex-direction: row;
    align-items: flex-start;
    gap: 0.85rem;
  }
  :global(.phone) .group-block { flex: 1 1 0; min-width: 0; }

  /* flex-shrink:0 — a flex column that scrolls must not squash its groups; the
     aspect-ratio tiles inside would otherwise flatten instead of overflowing. */
  .group-block { display: flex; flex-direction: column; gap: 0.65rem; flex-shrink: 0; }

  /* Era header: name + tagline on the left, selection status on the right, cyan rule under. */
  .era-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 0.45rem;
    border-bottom: 1px solid color-mix(in oklab, var(--color-cyan) 22%, transparent);
  }
  .era-titles { display: flex; align-items: baseline; gap: 0.6rem; min-width: 0; }
  .era-label {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 1.02rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-cyan);
    text-shadow: 0 0 10px color-mix(in oklab, var(--color-cyan) 35%, transparent);
    white-space: nowrap;
  }
  .era-tag {
    font-size: 0.78rem;
    color: var(--color-ink-dim);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .era-status {
    flex-shrink: 0;
    font-family: var(--font-display);
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: var(--color-ink-dim);
    transition: color 0.25s ease;
  }
  .era-status.done { color: var(--color-lime); }
  .era-status .tick { font-size: 0.85em; }

  /* Adapts to whatever number of decks an era offers: one centred row on desktop,
     wraps cleanly below. auto-fit collapses empty tracks so a 5-deck era isn't left-skewed. */
  .deck-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 158px));
    justify-content: center;
    gap: 0.7rem;
  }
  /* Smaller tiles on a phone: more decks per row = less scrolling in a short shell. */
  :global(.phone) .deck-row {
    grid-template-columns: repeat(auto-fit, minmax(88px, 108px));
    gap: 0.45rem;
  }
  :global(.phone) .era-head { padding-bottom: 0.3rem; }
  :global(.phone) .era-label { font-size: 0.82rem; }
  :global(.phone) .era-tag { display: none; }
  :global(.phone) .name { font-size: 0.72rem; }
  :global(.phone) .meta { padding: 0.4rem 0.45rem; }

  /* ── The tile: a square ace-art hero framed as a chamfered data-window. Dim at rest,
     ignites on hover/focus, stays LIT + badged when selected. ── */
  .tile {
    position: relative;
    aspect-ratio: 1 / 1;
    width: 100%;
    overflow: hidden;
    background: #0a1020;
    box-shadow:
      inset 0 0 0 1.5px color-mix(in oklab, var(--color-cyan) 30%, transparent),
      inset 0 2px 0 -0.5px color-mix(in oklab, var(--color-cyan) 38%, transparent);
    cursor: pointer;
    transition:
      transform 0.32s cubic-bezier(0.16, 1, 0.3, 1),
      box-shadow 0.32s ease,
      filter 0.32s ease,
      opacity 0.32s ease;
    animation: tile-in 0.42s cubic-bezier(0.16, 1, 0.3, 1) backwards;
    animation-delay: calc(var(--i) * 40ms);
  }
  .tile:hover,
  .tile:focus-visible {
    transform: translateY(-5px);
    box-shadow:
      inset 0 0 0 1.5px var(--color-cyan),
      0 0 0 1px color-mix(in oklab, var(--color-cyan) 30%, transparent);
    filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.55)) drop-shadow(0 0 16px color-mix(in oklab, var(--color-cyan) 38%, transparent));
    outline: none;
  }
  /* Committed selection, brighter edge + sustained glow (persists without hover). */
  .tile.selected {
    box-shadow:
      inset 0 0 0 2px var(--color-cyan),
      0 0 0 1px color-mix(in oklab, var(--color-cyan) 55%, transparent),
      0 0 22px color-mix(in oklab, var(--color-cyan) 32%, transparent);
  }
  .tile.selected:hover,
  .tile.selected:focus-visible { transform: translateY(-3px); }
  /* The unchosen siblings recede once a pick is made, so the choice reads as exclusive. */
  .tile.dimmed { opacity: 0.42; filter: saturate(0.72); }
  .tile.dimmed:hover,
  .tile.dimmed:focus-visible { opacity: 1; filter: none; }
  .tile:disabled { cursor: default; }

  .art {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: scale(1.01);
    transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .art-empty { background: radial-gradient(120% 120% at 50% 30%, #1a2748, #0a1020); }
  .tile:hover .art,
  .tile:focus-visible .art,
  .tile.selected .art { transform: scale(1.08); }

  /* legibility scrim, darkens only the lower third where the name sits */
  .scrim {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      to top,
      var(--color-void) 2%,
      color-mix(in oklab, var(--color-void) 78%, transparent) 24%,
      color-mix(in oklab, var(--color-void) 18%, transparent) 46%,
      transparent 64%
    );
  }
  .corner {
    position: absolute;
    inset: 0 0 auto 0;
    height: 34%;
    pointer-events: none;
    background: linear-gradient(to bottom, color-mix(in oklab, var(--color-cyan) 14%, transparent), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  .tile:hover .corner,
  .tile:focus-visible .corner,
  .tile.selected .corner { opacity: 1; }

  /* Selected check badge, top-right, filled cyan, pops in. */
  .check {
    position: absolute;
    top: 0.42rem;
    right: 0.42rem;
    display: grid;
    place-items: center;
    width: 1.3rem;
    height: 1.3rem;
    border-radius: 999px;
    background: var(--color-cyan);
    color: var(--color-void);
    font-size: 0.8rem;
    font-weight: 900;
    line-height: 1;
    box-shadow: 0 0 12px color-mix(in oklab, var(--color-cyan) 70%, transparent);
    opacity: 0;
    transform: scale(0.4);
    transition: opacity 0.2s ease, transform 0.28s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: none;
  }
  .tile.selected .check { opacity: 1; transform: scale(1); }

  .meta {
    position: absolute;
    inset: auto 0 0 0;
    padding: 0.6rem 0.65rem 0.6rem;
  }
  .name {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 0.92rem;
    line-height: 1.08;
    letter-spacing: 0.01em;
    color: var(--color-ink);
    text-shadow: 0 1px 5px rgba(0, 0, 0, 0.95), 0 0 14px rgba(0, 0, 0, 0.7);
    text-wrap: balance;
  }

  /* hover scanline sweep, "Motion = data" */
  .scan {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(115deg, transparent 42%, color-mix(in oklab, var(--color-cyan) 28%, transparent) 50%, transparent 58%);
    background-size: 250% 250%;
    background-position: 130% 0;
    opacity: 0;
    mix-blend-mode: screen;
  }
  .tile:hover .scan,
  .tile:focus-visible .scan {
    opacity: 1;
    animation: scan-sweep 0.7s cubic-bezier(0.16, 1, 0.3, 1);
  }

  /* ── Confirm bar ── */
  .confirm-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 0.4rem;
    padding: 0.85rem 1.5rem;
    border-top: 1px solid color-mix(in oklab, var(--color-cyan-dim) 30%, transparent);
    background: color-mix(in oklab, var(--color-void) 40%, transparent);
  }
  :global(.phone) .confirm-bar { padding: 0.5rem 0.85rem; gap: 0.6rem; }
  :global(.phone) .claim-cta { padding: 0.55rem 1.1rem; }
  .hint {
    font-size: 0.82rem;
    color: var(--color-ink-dim);
    text-wrap: balance;
  }
  .hint.err { color: var(--color-magenta); }
  .claim-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    padding: 0.65rem 1.75rem;
    border: 1.5px solid var(--color-cyan);
    background: color-mix(in oklab, var(--color-cyan) 16%, var(--color-surface-2));
    font-family: var(--font-display);
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: var(--color-cyan);
    box-shadow: 0 0 16px color-mix(in oklab, var(--color-cyan) 28%, transparent);
    transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
  }
  .claim-cta:not(:disabled):hover {
    background: color-mix(in oklab, var(--color-cyan) 28%, var(--color-surface-2));
    box-shadow: 0 0 24px color-mix(in oklab, var(--color-cyan) 45%, transparent);
  }
  .claim-cta:not(:disabled):active { transform: translateY(1px); }
  .claim-cta:disabled {
    cursor: default;
    border-color: color-mix(in oklab, var(--color-cyan) 30%, transparent);
    background: color-mix(in oklab, var(--color-surface-2) 70%, transparent);
    color: var(--color-ink-dim);
    box-shadow: none;
    opacity: 0.75;
  }

  /* ── Acquired view ── */
  .acquired-list { display: flex; flex-direction: column; gap: 0.7rem; }
  .acquired-row {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 0.55rem 0.7rem;
    background: color-mix(in oklab, var(--color-surface-2) 55%, transparent);
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--color-cyan) 18%, transparent);
  }
  .acquired-hero {
    flex-shrink: 0;
    width: 3.5rem;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    box-shadow: inset 0 0 0 1.5px color-mix(in oklab, var(--color-cyan) 40%, transparent);
  }
  .acquired-era {
    font-family: var(--font-display);
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: var(--color-cyan);
  }
  .acquired-name {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 1.05rem;
    line-height: 1.1;
    color: var(--color-ink);
  }
  .acquired-count { font-size: 0.78rem; color: var(--color-ink-dim); }

  .ring {
    width: 2.1rem;
    height: 2.1rem;
    border-radius: 50%;
    border: 2px solid color-mix(in oklab, var(--color-cyan) 22%, transparent);
    border-top-color: var(--color-cyan);
    animation: ring-spin 0.7s linear infinite;
  }
  .ring.small {
    width: 1rem;
    height: 1rem;
    border-width: 2px;
    border-top-color: currentColor;
  }

  @keyframes tile-in {
    from { opacity: 0; transform: translateY(10px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes scan-sweep {
    from { background-position: 130% 0; }
    to { background-position: -40% 0; }
  }
  @keyframes ring-spin { to { transform: rotate(360deg); } }

  @media (prefers-reduced-motion: reduce) {
    .tile { animation: none; transition: box-shadow 0.2s ease, opacity 0.2s ease; }
    .tile:hover, .tile:focus-visible, .tile.selected:hover, .tile.selected:focus-visible { transform: none; }
    .art, .tile:hover .art, .tile:focus-visible .art, .tile.selected .art { transition: none; transform: scale(1.01); }
    .scan { display: none; }
    .check { transition: opacity 0.15s ease; transform: scale(1); }
    .ring { animation-duration: 1.4s; }
  }
</style>
